// @ts-strict-ignore
import { inject, Injectable } from '@angular/core';
import { DEFAULT_TRAJECTORY_FUNCTION_TYPE } from '../../../../app.constants';
import { setUnsetParameters } from '../../../../functions/board-parameters/set-unset-parameters';
import { holdsActiveCustomModel } from '../../../../functions/client-model/client-node/holds-active-custom-model';
import { getHistoricalYearsFromTimeline } from '../../../../functions/timeline/get-historical-years-from-timeline';
import { getTransitionYearsFromTimeline } from '../../../../functions/timeline/get-transition-years-from-timeline';
import { filterCccData } from '../../../../functions/transition-targets/filter-ccc-data';
import { sumArray } from '../../../../functions/utilities/array-utility';
import { BoardTimeline } from '../../../../model/board-timeline';
import { ClientNode } from '../../../../model/client-model/client-node';
import { ClientParameter } from '../../../../model/client-model/client-parameter';
import { CustomTargetModelData } from '../../../../model/custom-model/custom-target-model-data';
import { Dict } from '../../../../model/dict';
import { EmissionFactorCalculationMethod } from '../../../../model/enums/emission-factor-calculation-method';
import { MeasurementSystem } from '../../../../model/enums/measurement-system';
import { TransitionElement } from '../../../../model/enums/transition-element';
import { ClientBoardContainer } from '../../../../public/dashboard/shared/model/client-board-container';
import { Tree } from '../../../../services/backend/model/tree';
import { CccAdapterTransitionTargetItem } from '../../../../services/climate-engine/ccc-adapter/ccc-adapter-transition-target-item';
import { CccAdapterService } from '../../../../services/climate-engine/ccc-adapter/ccc-adapter.service';
import { getCarbonAbatement } from '../../../../services/climate-engine/ccc-algorithm/get-carbon-abatement';
import { TransitionData } from '../../../../services/climate-engine/ccc-algorithm/transition-data';
import { CCCModelUtility } from '../../../../services/climate-engine/ccc-model-utility/ccc-model-utility';
import { CalculationResult, CccData } from '../../../../services/climate-engine/model/calculation-result';
import { isMetricEnergyUnit } from '../../../../services/unit-conversion/is-metric-energy-unit';
import { UnitConversionService } from '../../../../services/unit-conversion/unit-conversion.service';
import { getLeafNodes } from '../../functions/get-leaf-nodes';
import { getTransitionTargets } from '../../functions/get-transition-targets';
import { createGetCustomModelWithAbatementPerCccCopPanorama } from './create-get-custom-model-with-abatement-per-ccc.cop-panorama';
import {
  DashboardReportedEmissionsCalculationResult,
  DashboardV4CalculationResult,
  DashboardV4ReportedEmissionsCalculationResult,
} from './dashboard-v4-calculation-result';
import { getCalculationResultId } from './get-calculation-result-id';
import { getCustomModelsWithAbatementPerCcc } from './get-custom-models-with-abatement-per-ccc';

export interface CustomModelWithAbatementPerCcc extends CustomTargetModelData {
  id: string;
  cccAbatements: { ccc: string; abatement: number }[];
}

type GetCustomModelWithAbatementPerCcc = (
  customModelData: (CustomTargetModelData & { id: string })[],
  year: number,
  cccEmissions: Dict<number>
) => CustomModelWithAbatementPerCcc[];

export const CUSTOM_MODEL_CCC_ID = 'custom_model_ccc_id' as const;
@Injectable({
  providedIn: 'root',
})
export class DashboardCalculationService {
  private _unitConversionService = inject(UnitConversionService);

  constructor(
    private _cccAdapterService: CccAdapterService,
    private _cccModelUtility: CCCModelUtility
  ) {}

  calculate(
    board: ClientBoardContainer,
    tree: Tree,
    /**
     * Will be removed when the full panorama tree is ready,
     */
    useCopPanoramaAbatement: boolean,
    /**
     * 'none' is the way it will be handled from v5 and forward.
     * 'onlyUnconnected' is the way it is handled in v4.
     */
    customModelEmissionCount: 'none' | 'onlyUnconnected'
  ): DashboardV4CalculationResult[] {
    const result: DashboardV4CalculationResult[] = [];
    const timeline: BoardTimeline = board.boardSettings.timeline;
    const years = getTransitionYearsFromTimeline(timeline);
    const leafNodes: ClientNode[] = getLeafNodes(board);
    const customModelData: (CustomTargetModelData & { id: string })[] = this._getCustomModelData(leafNodes);
    let customModelDataWithReferencesToCustomCcc: (CustomTargetModelData & { id: string })[] = undefined;
    let scope1Cccs: string[] = undefined;

    //bau scenario
    this._initCccAdapterService([], timeline, board.boardSettings.electricityEmissionFactorType, board.parameters);
    years.forEach((y, t) => {
      const calculationResult: CalculationResult = this._cccAdapterService.calculate(t);
      if (scope1Cccs === undefined) {
        scope1Cccs = Object.keys(filterCccData(calculationResult.cccData, tree)); // the list of cccs is the same for all years, and in all scenarios, so we only need to get it once.
      }
      if (customModelDataWithReferencesToCustomCcc === undefined) {
        customModelDataWithReferencesToCustomCcc = this._getCustomModelDataWithReferencesToCustomCcc(
          customModelData,
          scope1Cccs
        ); // the list of custom models without cccs is the same for all years, and in all scenarios, so we only need to get it once.
      }

      const cccEmissions: Dict<{
        bau: number;
        fullTransition: number;
        fullTransitionEnergyModelAbatement: number;
        onlyUtilisationTransition: number;
      }> = this._getBauScenarioCccData(scope1Cccs, calculationResult);
      cccEmissions[CUSTOM_MODEL_CCC_ID] = cccEmissions[CUSTOM_MODEL_CCC_ID] = {
        bau: 0,
        fullTransition: 0,
        fullTransitionEnergyModelAbatement: 0,
        onlyUtilisationTransition: 0,
      };
      if (customModelEmissionCount === 'onlyUnconnected') {
        cccEmissions[CUSTOM_MODEL_CCC_ID] = this._getCustomCccEmissions(customModelDataWithReferencesToCustomCcc, y);
      }
      result.push({ cccEmissions, teCarbonAbatement: {}, teTransitions: {} });
    });

    let getCustomAbatement: GetCustomModelWithAbatementPerCcc = getCustomModelsWithAbatementPerCcc;
    if (useCopPanoramaAbatement) {
      const lookup = leafNodes.reduce((dict, node) => {
        dict[node.id] = node.nodeProperties?.transitionElement ?? node.id;
        return dict;
      }, {});
      getCustomAbatement = createGetCustomModelWithAbatementPerCccCopPanorama(lookup);
    }

    // transition scenario
    const targets: CccAdapterTransitionTargetItem[] = this._getTargets(board);
    this._initCccAdapterService(targets, timeline, board.boardSettings.electricityEmissionFactorType, board.parameters);
    years.forEach((y, t) => {
      const currentResult = result[t];
      const calculationResult: CalculationResult = this._cccAdapterService.calculate(t);
      this._addTransitionData(
        currentResult,
        calculationResult,
        board.boardSettings.measurementSystem ?? MeasurementSystem.Metric
      );
      this._addTransitionScenarioData(
        scope1Cccs,
        currentResult,
        calculationResult,
        customModelDataWithReferencesToCustomCcc,
        y,
        getCustomAbatement
      );
    });

    // only utilisation side scenario
    const utilisationTargets: CccAdapterTransitionTargetItem[] = targets.filter((t) =>
      this._cccModelUtility.isUtilisationTransitionElement(t.transitionElement)
    );
    this._initCccAdapterService(
      utilisationTargets,
      timeline,
      board.boardSettings.electricityEmissionFactorType,
      board.parameters
    );
    years.forEach((y, t) => {
      const currentResult = result[t];
      const calculationResult: CalculationResult = this._cccAdapterService.calculate(t);
      this._addOnlyUtilisationSideScenarioData(
        scope1Cccs,
        currentResult,
        calculationResult,
        customModelDataWithReferencesToCustomCcc,
        y,
        getCustomAbatement
      );
    });
    return result;
  }

  calculateReportedEmissions(board: ClientBoardContainer, tree: Tree): DashboardReportedEmissionsCalculationResult {
    const timeline: BoardTimeline = board.boardSettings.timeline;
    const years = getHistoricalYearsFromTimeline(timeline);
    let scope1Cccs: string[] = undefined;

    let historicalEmissions: Dict<Dict<number>> = {};
    for (const year of years) {
      //bau scenario
      let inventory;
      for (const inv of board.inventories.values()) {
        if (inv.inventoryYear === year) {
          inventory = inv;
        }
      }
      if (!inventory) {
        continue;
      }
      this._initCccAdapterService([], timeline, board.boardSettings.electricityEmissionFactorType, inventory.variables);
      const calculationResult: CalculationResult = this._cccAdapterService.calculate(0);
      if (scope1Cccs === undefined) {
        scope1Cccs = Object.keys(filterCccData(calculationResult.cccData, tree)); // the list of cccs is the same for all years, and in all scenarios, so we only need to get it once.
      }
      const cccEmissions: Dict<{ bau: number }> = this._getBauScenarioCccData(scope1Cccs, calculationResult);
      historicalEmissions[year] = Object.keys(cccEmissions).reduce((emissions, ccc) => {
        emissions[ccc] = cccEmissions[ccc].bau;
        return emissions;
      }, {});
    }
    return { inventoryEmissions: historicalEmissions };
  }

  calculateReportedEmissionsV4(board: ClientBoardContainer, tree: Tree): DashboardV4ReportedEmissionsCalculationResult {
    const timeline: BoardTimeline = board.boardSettings.timeline;
    const years = getHistoricalYearsFromTimeline(timeline);
    let scope1Cccs: string[] = undefined;

    let historicalEmissions: Dict<number> = {};
    for (const year of years) {
      //bau scenario
      let inventory;
      for (const inv of board.inventories.values()) {
        if (inv.inventoryYear === year) {
          inventory = inv;
        }
      }
      if (!inventory) {
        continue;
      }
      this._initCccAdapterService([], timeline, board.boardSettings.electricityEmissionFactorType, inventory.variables);
      const calculationResult: CalculationResult = this._cccAdapterService.calculate(0);
      if (scope1Cccs === undefined) {
        scope1Cccs = Object.keys(filterCccData(calculationResult.cccData, tree)); // the list of cccs is the same for all years, and in all scenarios, so we only need to get it once.
      }
      const cccEmissions: Dict<{ bau: number }> = this._getBauScenarioCccData(scope1Cccs, calculationResult);
      historicalEmissions[year] = sumArray(Object.values(cccEmissions).map((ccc) => ccc.bau));
    }
    return { inventoryEmissions: historicalEmissions };
  }

  private _addOnlyUtilisationSideScenarioData(
    scope1Cccs: string[],
    currentResult: DashboardV4CalculationResult,
    calculationResult: CalculationResult,
    customModelData: (CustomTargetModelData & { id: string })[],
    y: number,
    getCustomAbatement: GetCustomModelWithAbatementPerCcc
  ): void {
    // add remaining ccc emissions to the result
    scope1Cccs.forEach((ccc) => {
      currentResult.cccEmissions[ccc].onlyUtilisationTransition = this._getRemainingEmissions(
        calculationResult.cccData[ccc]
      );
    });
    // add te carbon abatement to the result
    Object.keys(calculationResult.transitions).forEach((te) => {
      currentResult.teCarbonAbatement[te].onlyUtilisation = getCarbonAbatement(calculationResult.transitions[te]);
    });
    const cccEmissions: Dict<number> = Object.entries(currentResult.cccEmissions).reduce((dict, [ccc, data]) => {
      dict[ccc] = data?.onlyUtilisationTransition ?? 0;
      return dict;
    }, {});
    // add custom models to the result;
    getCustomAbatement(customModelData, y, cccEmissions).forEach((cm) => {
      //adjust cccs with abatement from custom models.
      cm.cccAbatements.forEach((cccAbatement) => {
        const cccId = currentResult.cccEmissions[cccAbatement.ccc] ? cccAbatement.ccc : CUSTOM_MODEL_CCC_ID;
        currentResult.cccEmissions[cccId].onlyUtilisationTransition -= cccAbatement.abatement;
      });
      //add custom model adjusted carbon abatement.
      currentResult.teCarbonAbatement[cm.id].onlyUtilisation = cm.chartData.carbonAbatement[y] ?? 0;
    });
  }

  private _addTransitionData(
    currentResult: DashboardV4CalculationResult,
    calculationResult: CalculationResult,
    measurementSystem: MeasurementSystem
  ) {
    const getPossiblyConvertedActivityValue = (te: TransitionElement, metricValue: number): number => {
      const metricUnit = this._cccModelUtility.getDefaultActivityUnit(te);
      const isEnergyUnit = isMetricEnergyUnit(metricUnit);
      if (isEnergyUnit) {
        const usEnergyUnit = this._cccModelUtility.getUSEnergyUnitOfTransitionElement(te);
        const value = this._unitConversionService.transformEnergyValue(
          MeasurementSystem.Metric,
          measurementSystem,
          metricUnit,
          usEnergyUnit,
          metricValue
        );
        return value;
      } else {
        return this._unitConversionService.transformValue(
          MeasurementSystem.Metric,
          measurementSystem,
          metricUnit,
          metricValue
        );
      }
    };

    Object.keys(calculationResult.transitions).forEach((te) => {
      const data: TransitionData = calculationResult.transitions[te];
      currentResult.teTransitions[te] = {
        target: getPossiblyConvertedActivityValue(te as TransitionElement, data.activityShift),
      };
    });
  }

  private _addTransitionScenarioData(
    scope1Cccs: string[],
    currentResult: DashboardV4CalculationResult,
    calculationResult: CalculationResult,
    customModelData: (CustomTargetModelData & { id: string })[],
    y: number,
    getCustomAbatement: GetCustomModelWithAbatementPerCcc
  ): void {
    // add remaining ccc emissions and energy model abatement to the result
    scope1Cccs.forEach((ccc) => {
      currentResult.cccEmissions[ccc].fullTransition = this._getRemainingEmissions(calculationResult.cccData[ccc]);
      currentResult.cccEmissions[ccc].fullTransitionEnergyModelAbatement =
        currentResult.cccEmissions[ccc].bau - calculationResult.cccData[ccc].bauEmissions;
    });
    // add te carbon abatement to the result
    Object.keys(calculationResult.transitions).forEach((te) => {
      currentResult.teCarbonAbatement[te] = {
        fullTransition: getCarbonAbatement(calculationResult.transitions[te]),
        onlyUtilisation: undefined,
      };
    });
    const cccEmissions: Dict<number> = Object.entries(currentResult.cccEmissions).reduce((dict, [ccc, data]) => {
      dict[ccc] = data?.fullTransition ?? 0;
      return dict;
    }, {});
    // add custom models to the result;
    getCustomAbatement(customModelData, y, cccEmissions).forEach((cm) => {
      //adjust cccs with abatement from custom models.
      cm.cccAbatements.forEach((cccAbatement) => {
        const cccId = currentResult.cccEmissions[cccAbatement.ccc] ? cccAbatement.ccc : CUSTOM_MODEL_CCC_ID;
        currentResult.cccEmissions[cccId].fullTransition -= cccAbatement.abatement;
      });
      //add custom model adjusted carbon abatement.
      currentResult.teCarbonAbatement[cm.id] = {
        fullTransition: cm.chartData.carbonAbatement[y] ?? 0,
        onlyUtilisation: undefined,
      };
    });
  }

  private _getBauScenarioCccData(
    scope1Cccs: string[],
    calculationResult: CalculationResult
  ): Dict<{
    bau: number;
    fullTransition: number;
    fullTransitionEnergyModelAbatement: number;
    onlyUtilisationTransition: number;
  }> {
    const cccEmissions: Dict<{
      bau: number;
      fullTransition: number;
      fullTransitionEnergyModelAbatement: number;
      onlyUtilisationTransition: number;
    }> = {};
    scope1Cccs.forEach((ccc) => {
      cccEmissions[ccc] = {
        bau: calculationResult.cccData[ccc].bauEmissions,
        fullTransition: 0,
        fullTransitionEnergyModelAbatement: 0,
        onlyUtilisationTransition: 0,
      };
    });
    return cccEmissions;
  }

  private _getCustomCccEmissions(
    customModelDatas: (CustomTargetModelData & { id: string })[],
    year: number
  ): {
    bau: number;
    fullTransition: number;
    fullTransitionEnergyModelAbatement: number;
    onlyUtilisationTransition: number;
  } {
    const customModelEmissions = customModelDatas.reduce((sum, data) => {
      const contributionShare = data.lhsCccs.filter((ccc) => ccc === CUSTOM_MODEL_CCC_ID).length / data.lhsCccs.length; // NB: all custom models going into this function are expected to have at least one entry with the custom model ccc id.
      const contribution = data.chartData.emissions[year] ? data.chartData.emissions[year] * contributionShare : 0;
      return sum + contribution;
    }, 0);
    return {
      bau: customModelEmissions,
      fullTransition: customModelEmissions,
      fullTransitionEnergyModelAbatement: 0,
      onlyUtilisationTransition: customModelEmissions,
    };
  }

  private _getCustomModelData(leafNodes: ClientNode[]): (CustomTargetModelData & { id: string })[] {
    return leafNodes.reduce((arr, node) => {
      if (holdsActiveCustomModel(node)) {
        arr.push({ ...node.nodeProperties.customTargetModelData, id: getCalculationResultId(node)! });
      }
      return arr;
    }, []);
  }

  private _getCustomModelDataWithReferencesToCustomCcc(
    customModelData: (CustomTargetModelData & { id: string })[],
    scope1Cccs: string[]
  ): (CustomTargetModelData & { id: string })[] {
    return customModelData.map((cm) => {
      if (!cm.lhsCccs || cm.lhsCccs.length === 0) {
        return { ...cm, lhsCccs: [CUSTOM_MODEL_CCC_ID] };
      }
      if (cm.lhsCccs && cm.lhsCccs.some((ccc) => !scope1Cccs.includes(ccc))) {
        //CccsPartiallyOutsideOfScope1
        return {
          ...cm,
          lhsCccs: cm.lhsCccs.map((ccc) => {
            return scope1Cccs.includes(ccc) ? ccc : CUSTOM_MODEL_CCC_ID; // replace cccs that are not in the scope with the custom model ccc id.
          }),
        };
      }
      return cm;
    });
  }

  private _getRemainingEmissions(cccData: CccData): number {
    return cccData.bauEmissions - sumArray(Object.values(cccData.carbonAbatement));
  }

  private _getTargets(board: ClientBoardContainer): CccAdapterTransitionTargetItem[] {
    const boardTargets = getTransitionTargets(board);
    const targets = boardTargets.map((target) => {
      return {
        targetLevel: target.nodeProperties.transitionTargetData.transitionProperties.targetLevel,
        transitionElement: target.nodeProperties.transitionTargetData.transitionTargetProperties.transitionElement,
        trajectoryFunctionType:
          target.nodeProperties.transitionTargetData.transitionProperties.transitionPath?.type ||
          DEFAULT_TRAJECTORY_FUNCTION_TYPE,
        manualPath: target.nodeProperties.transitionTargetData.transitionProperties.transitionPath?.manualPath,
      };
    });

    return targets;
  }

  private _initCccAdapterService(
    targets: CccAdapterTransitionTargetItem[],
    timeline: BoardTimeline,
    electricityEmissionFactorType: EmissionFactorCalculationMethod,
    parameters: ClientParameter[]
  ): void {
    this._cccAdapterService.init(
      { electricityEmissionFactorType },
      targets,
      setUnsetParameters(parameters),
      timeline,
      true
    );
  }
}
